import React, { useEffect, useState } from 'react';
import { Toggle } from './pageLayout';
import useIsMobile from './hooks/useIsMobile';

// station-user-settings: the signed-in user's own settings for a station, from the
// station menu. The form is built from the settings the station's helpers declare; only
// fields the user changes are sent, so an untouched one keeps following its default.

// Whether the station's menu offers "Station settings": one of its helpers declares user
// settings (the agent marks the station).
export function hasStationSettings(station) {
  return !!(station && station.has_user_settings);
}

const url = (stationId) => `/api/stations/${encodeURIComponent(stationId)}/my-settings`;

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '0.9rem',
  border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff',
};

// A `map` setting keyed by account (StationChat's aliases): one row per person, plus a
// row to add someone by handle. The server turns "@handle" keys into account ids.
function PeopleMapField({ setting, value, people, onChange }) {
  const [handle, setHandle] = useState('');
  const [text, setText] = useState('');
  const map = value || {};
  const who = (key) => {
    if (key.startsWith('@')) return key;
    const p = (people || {})[key];
    return p ? (p.full_name || p.handle || key) : key;
  };
  const add = () => {
    const h = handle.trim().replace(/^@/, '');
    if (!h || !text.trim()) return;
    onChange({ ...map, [`@${h}`]: text.trim() });
    setHandle('');
    setText('');
  };
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>{setting.label}</div>
      {setting.help && (
        <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px' }}>{setting.help}</div>
      )}
      {Object.entries(map).map(([key, alias]) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: '0.85rem', color: '#334155' }}>{who(key)}</span>
          <input aria-label={`Alias for ${who(key)}`} value={alias} style={{ ...inputStyle, flex: 1 }}
                 onChange={(e) => onChange({ ...map, [key]: e.target.value })} />
          <button type="button" aria-label={`Remove ${who(key)}`}
                  onClick={() => { const next = { ...map }; delete next[key]; onChange(next); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
        <input aria-label="Their handle" placeholder="@handle" value={handle} style={{ ...inputStyle, flex: 1 }}
               onChange={(e) => setHandle(e.target.value)} />
        <input aria-label="Your name for them" placeholder="Alias" value={text} style={{ ...inputStyle, flex: 1 }}
               onChange={(e) => setText(e.target.value)} />
        <button type="button" onClick={add} disabled={!handle.trim() || !text.trim()}
                style={{ padding: '0 10px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff',
                         cursor: handle.trim() && text.trim() ? 'pointer' : 'default' }}>Add</button>
      </div>
    </div>
  );
}

function Field({ setting, value, onChange, idPrefix = '', people }) {
  if (setting.type === 'map') {
    return <PeopleMapField setting={setting} value={value} people={people} onChange={onChange} />;
  }
  const id = `station-setting-${idPrefix ? `${idPrefix}-` : ''}${setting.key}`;
  let input;
  if (setting.type === 'boolean') {
    input = (
      <Toggle id={id} checked={!!value} onChange={(e) => onChange(e.target.checked)} />
    );
  } else if (setting.type === 'select') {
    input = (
      <select id={id} value={value ?? ''} style={inputStyle}
              onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}>
        {value == null && <option value="">(not set)</option>}
        {setting.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else {
    input = (
      <input id={id} type={setting.type === 'number' ? 'number' : 'text'} value={value ?? ''}
             style={inputStyle}
             onChange={(e) => {
               const raw = e.target.value;
               if (raw === '') onChange(null);
               else onChange(setting.type === 'number' ? Number(raw) : raw);
             }} />
    );
  }
  const inline = setting.type === 'boolean';
  return (
    <div style={{ marginTop: '12px' }}>
      <label htmlFor={id} style={{
        display: 'flex', flexDirection: inline ? 'row-reverse' : 'column',
        justifyContent: inline ? 'flex-end' : undefined, alignItems: inline ? 'center' : 'stretch',
        gap: inline ? '8px' : '4px', fontSize: '0.85rem', fontWeight: 600, color: '#0f172a',
      }}>
        {setting.label}
        {input}
      </label>
      {setting.help && (
        <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px' }}>{setting.help}</div>
      )}
    </div>
  );
}

// station-settings-mobile-sheet: on mobile the dialog is a bottom sheet over the input box.
export function settingsLayout(isMobile) {
  if (!isMobile) {
    return {
      overlay: { alignItems: 'center', padding: '16px' },
      panel: { borderRadius: '14px', maxWidth: '420px', maxHeight: '85vh' },
    };
  }
  return {
    overlay: { alignItems: 'flex-end', padding: 0 },
    panel: {
      borderRadius: '14px 14px 0 0', maxWidth: 'none', maxHeight: '75dvh',
      paddingBottom: 'calc(18px + env(safe-area-inset-bottom))',
    },
  };
}

export default function StationSettingsModal({ stationId, stationName, onClose, onSaved }) {
  const layout = settingsLayout(useIsMobile());
  const [state, setState] = useState({ status: 'loading' });
  // Changes by function set, then key.
  const [pendingSets, setPendingSets] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(url(stationId), { credentials: 'same-origin' })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) { setState({ status: 'signedOut' }); return; }
        if (!res.ok) { setState({ status: 'failed' }); return; }
        const body = await res.json();
        setState({ status: 'ready', sets: body.sets || [] });
      })
      .catch(() => { if (!cancelled) setState({ status: 'failed' }); });
    return () => { cancelled = true; };
  }, [stationId]);

  const changeInSet = (set, key, value) => {
    setPendingSets((c) => ({ ...c, [set]: { ...(c[set] || {}), [key]: value } }));
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(url(stationId), {
        method: 'PUT', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sets: pendingSets }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Couldn’t save your settings.');
      if (onSaved) onSaved(body);
      onClose();
    } catch (e) {
      setError(e.message || 'Couldn’t save your settings.');
      setBusy(false);
    }
  };

  let content;
  if (state.status === 'loading') {
    content = <p style={{ color: '#64748b', fontSize: '0.88rem' }}>Loading…</p>;
  } else if (state.status === 'signedOut') {
    content = (
      <p style={{ color: '#475569', fontSize: '0.88rem' }}>
        Log in to keep your own settings for this station. Until then it uses its defaults.
      </p>
    );
  } else if (state.status === 'failed') {
    content = <p style={{ color: '#b91c1c', fontSize: '0.88rem' }}>Couldn’t load your settings.</p>;
  } else if (!state.sets.length) {
    content = <p style={{ color: '#475569', fontSize: '0.88rem' }}>This station has no settings.</p>;
  } else {
    const dirty = Object.keys(pendingSets).length > 0;
    content = (
      <>
        {state.sets.map((set) => set.settings.map((s) => {
          const pending = pendingSets[set.name] || {};
          return (
            <Field key={`${set.name}.${s.key}`} setting={s} idPrefix={set.name} people={set.people}
                   value={s.key in pending ? pending[s.key] : set.values[s.key]}
                   onChange={(v) => changeInSet(set.name, s.key, v)} />
          );
        }))}
        {error && <div role="alert" style={{ color: '#b91c1c', fontSize: '0.82rem', marginTop: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
          <button type="button" onClick={save} disabled={!dirty || busy}
                  style={{
                    padding: '8px 16px', border: 'none', borderRadius: '8px', fontWeight: 600,
                    fontSize: '0.9rem', cursor: dirty && !busy ? 'pointer' : 'default',
                    background: dirty ? '#0084ff' : '#e2e8f0', color: dirty ? '#fff' : '#64748b',
                  }}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </>
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Station settings"
         style={{
           // Above the desktop rail (900), like the other dialogs raised from it.
           position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(15, 23, 42, 0.45)',
           display: 'flex', justifyContent: 'center', ...layout.overlay,
         }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div data-testid="station-settings-panel" style={{
        background: '#fff', padding: '18px', width: '100%', overflowY: 'auto',
        boxShadow: '0 12px 40px rgba(0,0,0,0.25)', ...layout.panel,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 style={{ margin: 0, fontSize: '1.05rem', color: '#0f172a' }}>Station settings</h2>
          <button type="button" onClick={onClose} aria-label="Close"
                  style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer',
                           color: '#64748b', lineHeight: 1 }}>×</button>
        </div>
        {stationName && (
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
            Your settings for {stationName}
          </div>
        )}
        {content}
      </div>
    </div>
  );
}
